

// Import custom hooks
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useWalletBalance, useCategories, useServices, useVideoData } from "../hooks/useOrderFormData";
import { useOrderFormState } from "../hooks/useOrderFormState";
import {
  useOrderFormCategoryParams,
  useOrderFormServiceParams,
  useOrderFormPriceCalculation,
  useOrderFormVideoVisibility
} from "../hooks/useOrderFormEffects";
import { extractVideoId } from "../utils/orderFormUtils"; // isValidQuantity is no longer needed here
import { OrderService } from "../services/orderService";

// Import components
import CategorySelector from "../components/OrderForm/CategorySelector";
import ServiceSelector from "../components/OrderForm/ServiceSelector";
import ServiceDetails from "../components/OrderForm/ServiceDetails";
import QuantityInput from "../components/OrderForm/QuantityInput";
import YoutubeUrlInput from "../components/OrderForm/YoutubeUrlInput";
import SubmitButton from "../components/OrderForm/SubmitButton";
import OrderSnackbar from "../components/OrderForm/OrderSnackbar";
import VideoPreview from "../components/OrderForm/VideoPreview"; // Assuming this component exists and is ready to use

function OrderForm() {
  const customer_id = localStorage.getItem("id");

  // Consolidated form state
  const {
    quantity, setQuantity,
    youtube_url, setYoutube_Url,
    service, setService,
    product_id, setProduct_id,
    service_type, setService_Type,
    serviceId, setServiceId,
    servicePrice, setServicePrice,
    price, setprice,
    category, selectedCategoryName, setCategory,
    error2, setError2,
    isLoad, setIsLoad,
    openSuccess, setOpenSuccess,
    openError, setOpenError,
    isButtonVisible, setIsButtonVisible,
    showFullDescription, setShowFullDescription,
    selectedServiceId, setSelectedServiceId,
    isOpenCat, setIsOpenCat,
    isOpen, setIsOpen,
    resetForm,
  } = useOrderFormState();

  // Track if user made any manual selection to stop URL processing
  const [hasManualSelection, setHasManualSelection] = useState(false);

  // Data fetching hooks
  const location = useLocation();
  const { walletBalance } = useWalletBalance();
  const { categories } = useCategories();
  const { serviceList } = useServices(category);
  const videoId = extractVideoId(youtube_url);
  const { videoData, isLoadingVideo } = useVideoData(videoId);

  const handleServiceSelect = (value, productId, item) => {
    setService(value); // service name
    setProduct_id(productId);
    setServiceId(item.id);
    setServicePrice(item.price);
    setService_Type(item.type);
    setSelectedServiceId(item.id); // Store the service ID
    setHasManualSelection(true); // Mark as manual selection
  };

  // Wrapper for category selection to track manual changes
  const handleCategorySelect = (newCategory) => {
    setCategory(newCategory);
    setHasManualSelection(true); // Mark as manual selection
  };
  // Effect hooks
  useOrderFormPriceCalculation(quantity, servicePrice, setprice);
  useOrderFormVideoVisibility(videoData, setIsButtonVisible);
  useOrderFormCategoryParams(setCategory, category, hasManualSelection);
  useOrderFormServiceParams(handleServiceSelect, serviceList, category, hasManualSelection);

  // Reset manual selection flag when URL changes (for page refresh)
  useEffect(() => {
    setHasManualSelection(false);
  }, [location.search]);



  // Event handlers
  const handleClick = () => {
    setOpenSuccess(false);
    setOpenError(false);
  };

  const handleVideoUrlChange = (event) => {
    setYoutube_Url(event.target.value);
  };

  const handlePaste = () => {
    // Video data fetching is handled automatically by useVideoData hook
  };

  const toggleDescription = (e) => {
    e.preventDefault();
    setShowFullDescription(!showFullDescription);
  };



  const handleQuantityChange = (e) => {
    setQuantity(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check wallet balance before submitting
    if (!walletBalance || walletBalance < parseFloat(price)) {
      setOpenError(true);
      setError2("Insufficient wallet balance. Please add funds to your wallet before placing the order.");
      return;
    }

    setIsLoad(true);

    try {
      const userId = localStorage.getItem("id");
      const result = await OrderService.processOrder({
        customer_id,
        service,
        product_id,
        quantity: parseInt(quantity),
        price: parseFloat(price),
        youtube_url,
        service_type,
        serviceId,
        userId,
        walletBalance
      });

      if (result.success) {
        setOpenSuccess(true);
        resetForm();
        window.location.href = "/order-history";
      } else {
        setOpenError(true);
        setError2(result.error);
      }
    } catch (error) {
      setOpenError(true);
      setError2("Failed to create order");
    } finally {
      setIsLoad(false);
    }
  };

  return (
    <>
      <div className="flex w-full flex-col gap-2 md:flex-row px-16 py-16">
        <div className="my-2 rounded-3xl bg-gradient-to-b from-sky-300 to-purple-500 p-px">
          <div className="w-full p-8 shadow-2xl bg-white rounded-[calc(1.5rem-1px)]">
            <form onSubmit={handleSubmit}>
              <div className="flex">
                <h2 className="font-medium uppercase text-2xl text-gray-600">
                  Add New Campaign
                </h2>
              </div>
              <CategorySelector
                selectedCategoryName={selectedCategoryName}
                categories={categories}
                isOpen={isOpenCat}
                onToggle={setIsOpenCat}
                onSelect={handleCategorySelect}
              />

              <ServiceSelector
                service={service}
                serviceList={serviceList}
                isOpen={isOpen}
                onToggle={setIsOpen}
                onSelect={handleServiceSelect}
              />

              <ServiceDetails
                service={service}
                serviceList={serviceList}
                selectedServiceId={selectedServiceId}
                service_type={service_type}
                servicePrice={servicePrice}
                showFullDescription={showFullDescription}
                toggleDescription={toggleDescription}
              />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 mt-5">
                <QuantityInput
                  quantity={quantity}
                  price={price}
                  handleQuantityChange={handleQuantityChange}
                />
              </div>

              <YoutubeUrlInput
                youtube_url={youtube_url}
                handleVideoUrlChange={handleVideoUrlChange}
                handlePaste={handlePaste}
              />

              <SubmitButton
                isButtonVisible={isButtonVisible}
                isLoad={isLoad}
              />
              <div className=" text-rose-500">{error2}</div>
            </form>
          </div>
        </div>
        <div className="w-full mx-auto sm:max-w-md">
          <VideoPreview
            isLoading={isLoadingVideo}
            videoData={videoData}
          />
        </div>
      </div>
      <OrderSnackbar
        openSuccess={openSuccess}
        openError={openError}
        handleClick={handleClick}
      />
    </>
  );
}

export default OrderForm;
